import { useState, useEffect, useCallback } from "react";
import { Plus, Pencil, Trash2, Building2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import api from "../lib/api";

const emptyForm = {
  name: "",
  code: "",
  address: "",
  phone: "",
  manager_name: "",
};

export default function Branches({ embedded = false }) {
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const fetchBranches = useCallback(async () => {
    try {
      const { data } = await api.get("/branches");
      setBranches(data);
    } catch {
      toast.error("Error al cargar sucursales");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBranches();
  }, [fetchBranches]);

  const openCreate = () => {
    setEditId(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEdit = (b) => {
    setEditId(b.id);
    setForm({
      name: b.name,
      code: b.code,
      address: b.address || "",
      phone: b.phone || "",
      manager_name: b.manager_name || "",
    });
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!form.name || !form.code) {
      toast.error("Nombre y codigo son requeridos");
      return;
    }
    try {
      if (editId) {
        await api.put(`/branches/${editId}`, form);
        toast.success("Sucursal actualizada");
      } else {
        await api.post("/branches", form);
        toast.success("Sucursal creada");
      }
      setDialogOpen(false);
      fetchBranches();
    } catch {
      toast.error("Error al guardar sucursal");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Eliminar esta sucursal?")) return;
    try {
      await api.delete(`/branches/${id}`);
      toast.success("Sucursal eliminada");
      fetchBranches();
    } catch {
      toast.error("Error al eliminar sucursal");
    }
  };

  return (
    <div data-testid="branches-page">
      <div className="flex items-center justify-between mb-6">
        {!embedded && (
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-zinc-950">
              Sucursales
            </h1>
            <p className="text-sm text-zinc-500 mt-1">
              Gestion de sucursales de la empresa
            </p>
          </div>
        )}
        {embedded && <div />}
        <Button
          onClick={openCreate}
          className="bg-zinc-900 text-white hover:bg-zinc-800"
          data-testid="add-branch-button"
        >
          <Plus className="w-4 h-4 mr-2" /> Nueva sucursal
        </Button>
      </div>

      <Card className="bg-white border-zinc-200 shadow-[0_1px_3px_rgba(0,0,0,0.02)]">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="border-zinc-100 hover:bg-transparent">
                <TableHead className="font-medium text-zinc-900 text-xs uppercase tracking-wide">
                  Codigo
                </TableHead>
                <TableHead className="font-medium text-zinc-900 text-xs uppercase tracking-wide">
                  Nombre
                </TableHead>
                <TableHead className="font-medium text-zinc-900 text-xs uppercase tracking-wide hidden sm:table-cell">
                  Direccion
                </TableHead>
                <TableHead className="font-medium text-zinc-900 text-xs uppercase tracking-wide hidden md:table-cell">
                  Telefono
                </TableHead>
                <TableHead className="font-medium text-zinc-900 text-xs uppercase tracking-wide hidden md:table-cell">
                  Encargado
                </TableHead>
                <TableHead className="font-medium text-zinc-900 text-xs uppercase tracking-wide">
                  Estado
                </TableHead>
                <TableHead className="font-medium text-zinc-900 text-xs uppercase tracking-wide text-right">
                  Acciones
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {branches.map((b) => (
                <TableRow
                  key={b.id}
                  className="border-zinc-100"
                  data-testid={`branch-row-${b.id}`}
                >
                  <TableCell className="text-sm text-zinc-600 font-mono">
                    {b.code}
                  </TableCell>
                  <TableCell className="text-sm text-zinc-900 font-medium">
                    {b.name}
                  </TableCell>
                  <TableCell className="text-sm text-zinc-500 hidden sm:table-cell">
                    {b.address || "\u2014"}
                  </TableCell>
                  <TableCell className="text-sm text-zinc-500 hidden md:table-cell">
                    {b.phone || "\u2014"}
                  </TableCell>
                  <TableCell className="text-sm text-zinc-500 hidden md:table-cell">
                    {b.manager_name || "\u2014"}
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant="outline"
                      className={
                        b.active !== false
                          ? "bg-emerald-50 text-emerald-700 border-emerald-200 font-medium"
                          : "bg-zinc-100 text-zinc-500 border-zinc-200 font-medium"
                      }
                    >
                      {b.active !== false ? "Activa" : "Inactiva"}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => openEdit(b)}
                      data-testid={`edit-branch-${b.id}`}
                    >
                      <Pencil className="w-4 h-4 text-zinc-400" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDelete(b.id)}
                      data-testid={`delete-branch-${b.id}`}
                    >
                      <Trash2 className="w-4 h-4 text-red-400" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {branches.length === 0 && !loading && (
                <TableRow>
                  <TableCell
                    colSpan={7}
                    className="text-center py-16 text-zinc-400"
                  >
                    <Building2 className="w-10 h-10 mx-auto mb-3 opacity-20" />
                    <p className="text-sm">No hay sucursales registradas</p>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-zinc-950">
              {editId ? "Editar sucursal" : "Nueva sucursal"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-medium text-zinc-700">
                  Codigo *
                </Label>
                <Input
                  value={form.code}
                  onChange={(e) => setForm({ ...form, code: e.target.value })}
                  placeholder="SUC-001"
                  data-testid="branch-code-input"
                />
              </div>
              <div>
                <Label className="text-xs font-medium text-zinc-700">
                  Nombre *
                </Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Sucursal Central"
                  data-testid="branch-name-input"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs font-medium text-zinc-700">
                Direccion
              </Label>
              <Input
                value={form.address}
                onChange={(e) => setForm({ ...form, address: e.target.value })}
                placeholder="San Jose, Costa Rica"
                data-testid="branch-address-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-medium text-zinc-700">
                  Telefono
                </Label>
                <Input
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  placeholder="+506 2222-3333"
                  data-testid="branch-phone-input"
                />
              </div>
              <div>
                <Label className="text-xs font-medium text-zinc-700">
                  Encargado
                </Label>
                <Input
                  value={form.manager_name}
                  onChange={(e) =>
                    setForm({ ...form, manager_name: e.target.value })
                  }
                  placeholder="Nombre del encargado"
                  data-testid="branch-manager-input"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDialogOpen(false)}
              data-testid="branch-cancel-button"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleSubmit}
              className="bg-zinc-900 text-white hover:bg-zinc-800"
              data-testid="branch-submit-button"
            >
              {editId ? "Actualizar" : "Crear"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
